import React, { useState, useEffect } from "react";
import {
  FiZap,
  FiCheck,
  FiX,
  FiLoader,
  FiDatabase,
  FiEdit3,
  FiList,
  FiTrendingUp,
  FiMessageCircle,
  FiLink,
  FiAlertCircle,
} from "react-icons/fi";
import { suggestionsService } from "../../services/suggestionsService";
import type {
  ContentSuggestion,
  SuggestionCategory,
  SuggestionPriority,
} from "../../types/suggestions";

// Type-safe icon wrappers
const ZapIcon = FiZap as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const XIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const LoaderIcon = FiLoader as React.FC<React.SVGProps<SVGSVGElement>>;
const DatabaseIcon = FiDatabase as React.FC<React.SVGProps<SVGSVGElement>>;
const EditIcon = FiEdit3 as React.FC<React.SVGProps<SVGSVGElement>>;
const ListIcon = FiList as React.FC<React.SVGProps<SVGSVGElement>>;
const TrendingUpIcon = FiTrendingUp as React.FC<React.SVGProps<SVGSVGElement>>;
const MessageIcon = FiMessageCircle as React.FC<React.SVGProps<SVGSVGElement>>;
const LinkIcon = FiLink as React.FC<React.SVGProps<SVGSVGElement>>;
const AlertIcon = FiAlertCircle as React.FC<React.SVGProps<SVGSVGElement>>;

// Chain of thought steps for suggestion analysis
const ANALYSIS_COT_STEPS = [
  "Analyzing draft content...",
  "Checking integration data...",
  "Identifying improvement opportunities...",
  "Generating suggestions...",
];

// Past tense versions
const PAST_TENSE_MAP: Record<string, string> = {
  Analyzing: "Analyzed",
  Checking: "Checked",
  Identifying: "Identified",
  Generating: "Generated",
};

const toPastTense = (step: string): string => {
  const firstWord = step.split(" ")[0];
  const pastTense = PAST_TENSE_MAP[firstWord];
  return pastTense ? step.replace(firstWord, pastTense) : step;
};

// Category icons
const getCategoryIcon = (category: SuggestionCategory) => {
  switch (category) {
    case "missing_data":
      return <DatabaseIcon style={{ width: 14, height: 14 }} />;
    case "clarity":
      return <EditIcon style={{ width: 14, height: 14 }} />;
    case "completeness":
      return <ListIcon style={{ width: 14, height: 14 }} />;
    case "metrics":
      return <TrendingUpIcon style={{ width: 14, height: 14 }} />;
    case "tone":
      return <MessageIcon style={{ width: 14, height: 14 }} />;
    case "integration_opportunity":
      return <LinkIcon style={{ width: 14, height: 14 }} />;
    default:
      return <ZapIcon style={{ width: 14, height: 14 }} />;
  }
};

// Category labels
const CATEGORY_LABELS: Record<SuggestionCategory, string> = {
  missing_data: "Missing Data",
  clarity: "Clarity",
  completeness: "Completeness",
  metrics: "Metrics",
  tone: "Tone",
  integration_opportunity: "Integration",
};

// Priority colors
const getPriorityColor = (priority: SuggestionPriority): string => {
  switch (priority) {
    case "high":
      return "#EF4444";
    case "medium":
      return "#F59E0B";
    case "low":
      return "#6B7280";
    default:
      return "#6B7280";
  }
};

// Priority labels
const PRIORITY_LABELS: Record<SuggestionPriority, string> = {
  high: "HIGH",
  medium: "MEDIUM",
  low: "LOW",
};

interface SuggestionsSidebarProps {
  /** Current editor content (HTML string) */
  content: string;
  /** Current email subject */
  subject?: string;
  /** Selected integration IDs to check for available data */
  integrationIds?: string[];
  /** Callback when user accepts a suggestion */
  onAcceptSuggestion: (suggestion: ContentSuggestion) => void;
  /** Whether to auto-analyze when content changes */
  autoAnalyze?: boolean;
  /** Debounce delay for auto-analyze (ms) */
  debounceMs?: number;
}

/**
 * SuggestionsSidebar - Displays AI-generated improvement suggestions
 * Analyzes draft content and shows actionable recommendations
 */
const SuggestionsSidebar: React.FC<SuggestionsSidebarProps> = ({
  content,
  subject,
  integrationIds = [],
  onAcceptSuggestion,
  autoAnalyze = false,
  debounceMs = 3000,
}) => {
  const [suggestions, setSuggestions] = useState<ContentSuggestion[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisSteps, setAnalysisSteps] = useState<string[]>([]);
  const [completedStepIndex, setCompletedStepIndex] = useState(-1);
  const [showProgress, setShowProgress] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const [lastAnalyzedContent, setLastAnalyzedContent] = useState<string>("");

  // Effect to mark steps complete during analysis
  useEffect(() => {
    if (!showProgress || analysisSteps.length === 0) return;

    const nextToComplete = completedStepIndex + 1;
    if (nextToComplete < analysisSteps.length) {
      const timer = setTimeout(() => {
        setCompletedStepIndex(nextToComplete);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [showProgress, analysisSteps.length, completedStepIndex]);

  // Auto-analyze effect (debounced)
  useEffect(() => {
    if (!autoAnalyze || !content || content.length < 50) return;
    if (content === lastAnalyzedContent) return;

    const timer = setTimeout(() => {
      handleAnalyze();
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [content, autoAnalyze, debounceMs, lastAnalyzedContent]);

  const handleAnalyze = async () => {
    if (!content || content.length < 10) {
      setError("Content too short for analysis");
      return;
    }

    setIsAnalyzing(true);
    setShowProgress(true);
    setAnalysisSteps([...ANALYSIS_COT_STEPS]);
    setCompletedStepIndex(-1);
    setError(null);

    try {
      const response = await suggestionsService.analyzeDraft(
        content,
        subject,
        integrationIds
      );

      if (response.success && response.data) {
        setSuggestions(response.data.suggestions);
        setLastAnalyzedContent(content);
      } else {
        setError(response.error || "Analysis failed");
      }
    } catch (err) {
      console.error("Suggestion analysis error:", err);
      setError("Failed to analyze content");
    } finally {
      // Wait for animation to complete
      setTimeout(() => {
        setIsAnalyzing(false);
        setShowProgress(false);
        setAnalysisSteps([]);
        setCompletedStepIndex(-1);
      }, 500);
    }
  };

  const handleDismiss = async (suggestion: ContentSuggestion) => {
    setDismissedIds((prev) => new Set(prev).add(suggestion.id));
    await suggestionsService.dismissSuggestion(suggestion.id);
  };

  const handleAccept = (suggestion: ContentSuggestion) => {
    onAcceptSuggestion(suggestion);
    // Remove from list after accepting
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
  };

  // Filter out dismissed suggestions
  const visibleSuggestions = suggestions.filter(
    (s) => !dismissedIds.has(s.id)
  );

  // Group suggestions by priority
  const highPriority = visibleSuggestions.filter((s) => s.priority === "high");
  const mediumPriority = visibleSuggestions.filter(
    (s) => s.priority === "medium"
  );
  const lowPriority = visibleSuggestions.filter((s) => s.priority === "low");

  return (
    <div className="su-suggestions-sidebar">
      {/* Header */}
      <div className="su-suggestions-header">
        <div className="su-suggestions-title">
          <ZapIcon style={{ width: 16, height: 16 }} />
          <span>SUGGESTIONS</span>
          {visibleSuggestions.length > 0 && (
            <span className="su-suggestions-count">
              {visibleSuggestions.length}
            </span>
          )}
        </div>
        <button
          type="button"
          className={`su-suggestions-analyze-btn ${isAnalyzing ? "loading" : ""}`}
          onClick={handleAnalyze}
          disabled={isAnalyzing || !content || content.length < 10}
        >
          {isAnalyzing ? (
            <LoaderIcon className="su-spinner" style={{ width: 14, height: 14 }} />
          ) : (
            "ANALYZE"
          )}
        </button>
      </div>

      {/* Progress indicator */}
      {showProgress && analysisSteps.length > 0 && (
        <div className="su-suggestions-progress">
          {analysisSteps.map((step, index) => {
            const isComplete = index <= completedStepIndex;
            const displayText = isComplete ? toPastTense(step) : step;

            return (
              <div
                key={index}
                className={`su-suggestions-step ${isComplete ? "completed" : ""}`}
              >
                <span className="su-suggestions-step-icon">
                  {isComplete ? (
                    <CheckIcon style={{ width: 12, height: 12, color: "#10B981" }} />
                  ) : (
                    <LoaderIcon
                      className="su-spinner"
                      style={{ width: 12, height: 12 }}
                    />
                  )}
                </span>
                <span className="su-suggestions-step-text">{displayText}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="su-suggestions-error">
          <AlertIcon style={{ width: 14, height: 14 }} />
          <span>{error}</span>
        </div>
      )}

      {/* Suggestions list */}
      {!showProgress && visibleSuggestions.length > 0 && (
        <div className="su-suggestions-list">
          {/* High priority */}
          {highPriority.length > 0 && (
            <div className="su-suggestions-group">
              {highPriority.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  onAccept={handleAccept}
                  onDismiss={handleDismiss}
                />
              ))}
            </div>
          )}

          {/* Medium priority */}
          {mediumPriority.length > 0 && (
            <div className="su-suggestions-group">
              {mediumPriority.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  onAccept={handleAccept}
                  onDismiss={handleDismiss}
                />
              ))}
            </div>
          )}

          {/* Low priority */}
          {lowPriority.length > 0 && (
            <div className="su-suggestions-group">
              {lowPriority.map((suggestion) => (
                <SuggestionCard
                  key={suggestion.id}
                  suggestion={suggestion}
                  onAccept={handleAccept}
                  onDismiss={handleDismiss}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!showProgress && !error && visibleSuggestions.length === 0 && (
        <div className="su-suggestions-empty">
          <p>
            {lastAnalyzedContent
              ? "No suggestions - your draft looks good!"
              : "Click ANALYZE to get AI-powered suggestions"}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Individual suggestion card component
 */
interface SuggestionCardProps {
  suggestion: ContentSuggestion;
  onAccept: (suggestion: ContentSuggestion) => void;
  onDismiss: (suggestion: ContentSuggestion) => void;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onAccept,
  onDismiss,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="su-suggestion-card">
      <div className="su-suggestion-card-header">
        <div className="su-suggestion-badges">
          <span
            className="su-suggestion-priority-badge"
            style={{ backgroundColor: getPriorityColor(suggestion.priority) }}
          >
            {PRIORITY_LABELS[suggestion.priority]}
          </span>
          <span className="su-suggestion-category-badge">
            {getCategoryIcon(suggestion.category)}
            <span>{CATEGORY_LABELS[suggestion.category]}</span>
          </span>
          {suggestion.sourceIntegration && (
            <span className="su-suggestion-source-badge">
              {suggestion.sourceIntegration}
            </span>
          )}
        </div>
        <button
          type="button"
          className="su-suggestion-dismiss-btn"
          onClick={() => onDismiss(suggestion)}
          title="Dismiss"
        >
          <XIcon style={{ width: 14, height: 14 }} />
        </button>
      </div>

      <h4 className="su-suggestion-title">{suggestion.title}</h4>
      <p className="su-suggestion-description">{suggestion.description}</p>

      {suggestion.suggestedText && (
        <>
          <button
            type="button"
            className="su-suggestion-expand-btn"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "Hide suggested text" : "Show suggested text"}
          </button>
          {isExpanded && (
            <div className="su-suggestion-text-preview">
              <p>{suggestion.suggestedText}</p>
            </div>
          )}
        </>
      )}

      <div className="su-suggestion-actions">
        <button
          type="button"
          className="su-suggestion-accept-btn"
          onClick={() => onAccept(suggestion)}
        >
          <CheckIcon style={{ width: 12, height: 12 }} />
          <span>{suggestion.suggestedText ? "INSERT" : "GOT IT"}</span>
        </button>
      </div>
    </div>
  );
};

export default SuggestionsSidebar;
